/**
 * Main navigation.
 *
 * Four tabs, matching what messaging apps have settled on — and one of them
 * is worth explaining, because it is where ZIvapp's plan for channels lives.
 *
 *   Chats        conversations
 *   Updates      status AND channels, together in one place
 *   Communities  groups of groups
 *   Calls        history, voice and video
 *
 * "Updates" rather than "Status" is current: WhatsApp renamed it when
 * channels launched, because one-to-many broadcasts and 24-hour statuses are
 * the same idea — things you publish rather than conversations you have.
 * Putting channels behind their own tab would split that in two.
 *
 * Deliberately NOT copied from current WhatsApp: the floating pill bar and
 * the circular tab indicator are both unreleased beta experiments. Matching
 * a design that may never ship is how you end up looking dated on launch day.
 */

import { NavLink } from 'react-router-dom'
import { useChats } from '../../context/index.js'
import './BottomNav.css'

const TABS = [
  {
    to: '/chats',
    label: 'Chats',
    icon: (
      <>
        <path d="M5 4h14a3 3 0 0 1 3 3v8a3 3 0 0 1-3 3H9l-5 4v-4a3 3 0 0 1-3-3V7a3 3 0 0 1 3-3Z"
          stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      </>
    )
  },
  {
    to: '/updates',
    label: 'Updates',
    icon: (
      <>
        {/* A ring, the shape a status has carried since stories existed. */}
        <circle cx="12" cy="12" r="8.4" stroke="currentColor" strokeWidth="1.8"
          strokeDasharray="4.2 2.6" />
        <circle cx="12" cy="12" r="3.2" stroke="currentColor" strokeWidth="1.8" />
      </>
    )
  },
  {
    to: '/communities',
    label: 'Communities',
    icon: (
      <>
        <path d="M4 19v-1.6a3.4 3.4 0 0 1 3.4-3.4h2.2a3.4 3.4 0 0 1 3.4 3.4V19"
          stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <circle cx="8.5" cy="8.6" r="3" stroke="currentColor" strokeWidth="1.8" />
        <path d="M16 6.2a3 3 0 0 1 0 5.6M17 14.2c2 .4 3.4 2 3.4 4V19"
          stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </>
    )
  },
  {
    to: '/calls',
    label: 'Calls',
    icon: (
      <>
        <path d="M6.6 3h3l1.5 4-2.2 1.6a12 12 0 0 0 5.5 5.5L16 11.9l4 1.5v3a2 2 0 0 1-2.2 2A16.5 16.5 0 0 1 4.6 5.2 2 2 0 0 1 6.6 3Z"
          stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      </>
    )
  }
]

export default function BottomNav () {
  const { totalUnread } = useChats()

  // Hard-wired off until the status service has a provider behind it on our
  // own server. Showing a permanent dot that never clears teaches people to
  // ignore it, which is the one thing a notification dot must not do.
  const hasNewUpdates = false

  return (
    <nav className="ziv-nav" aria-label="Main">
      {TABS.map(tab => (
        <NavLink
          key={tab.to}
          to={tab.to}
          className={({ isActive }) => `ziv-nav__tab${isActive ? ' is-active' : ''}`}
        >
          <span className="ziv-nav__icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none">{tab.icon}</svg>
            {tab.to === '/chats' && totalUnread > 0 && (
              <span className="ziv-nav__badge">{totalUnread > 99 ? '99+' : totalUnread}</span>
            )}
            {/* A dot rather than a count: "something new" is all the Updates
                tab can usefully say, since statuses are not read one by one
                the way messages are. */}
            {tab.to === '/updates' && hasNewUpdates && <span className="ziv-nav__dot" />}
          </span>
          <span className="ziv-nav__label">{tab.label}</span>
        </NavLink>
      ))}
    </nav>
  )
}
